import { B, loc } from '@/i18n';
import type {
  AssessmentStatus, CurriculumRelevance, Difficulty, EvidenceRecord, EvidenceSource, Skill, SkillEstimate, SkillView,
} from '@/core/types';
import { DAY, clamp, clamp01 } from '@/core/util/misc';
import { diffuse, mean, neutralPrior, quantile, sd, update } from './ability';
import { newMemory, retrievability, review } from './srs';

/**
 * SKILL ESTIMATES — evidence in, estimate out.
 * Only real evidence (answers in diagnostics, lessons, practice, reviews,
 * exams, courses, boss battles …) can create or change an estimate.
 */

export const SOURCE_WEIGHT: Record<EvidenceSource, number> = {
  diagnostic: 1, exam: 1, boss: 1, assessment: 1, application: 0.9, review: 0.9, practice: 0.85, course: 0.85,
  lesson: 0.75, feynman: 0.6, guided: 0.5, flashcard: 0.4,
};

export interface EvidenceInput {
  skillId: string;
  difficulty: number;
  correct: boolean;
  score: number;
  hintsUsed: number;
  responseMs: number;
  confidence?: number; // 1..4
  source: EvidenceSource;
  fingerprint: string;
  guess: number;
  at: number;
  misconceptionId?: string;
}

export function newEstimate(skillId: string): SkillEstimate {
  return {
    skillId,
    posterior: neutralPrior(),
    evidenceCount: 0,
    attempts: 0,
    correct: 0,
    firstAssessed: null,
    lastAssessed: null,
    recent: [],
    hintsUsed: 0,
    totalResponseMs: 0,
    calibration: { n: 0, brierSum: 0, overconfident: 0, underconfident: 0 },
    misconceptions: {},
    maxIndependentDifficulty: 0,
    memory: newMemory(),
  };
}

const CONF_P = [0, 0.3, 0.55, 0.78, 0.95];

export function applyEvidence(prev: SkillEstimate | undefined, ev: EvidenceInput): SkillEstimate {
  const est = prev ? { ...prev } : newEstimate(ev.skillId);
  // time decay of certainty since last evidence
  if (est.lastAssessed) est.posterior = diffuse(est.posterior, (ev.at - est.lastAssessed) / DAY);

  let weight = SOURCE_WEIGHT[ev.source] ?? 0.8;
  weight *= 1 / (1 + 0.5 * ev.hintsUsed);
  const score = ev.correct ? clamp(1 - 0.25 * ev.hintsUsed, 0.4, 1) : clamp(ev.score * 0.6, 0, 0.6);
  // a confident correct answer on multiple-choice is stronger evidence than a guess
  const guess = ev.confidence && ev.confidence <= 1 ? Math.min(0.5, ev.guess + 0.15) : ev.guess;
  est.posterior = update(est.posterior, ev.difficulty, score, weight, guess);

  est.evidenceCount += 1;
  est.attempts += 1;
  if (ev.correct) est.correct += 1;
  est.hintsUsed += ev.hintsUsed;
  est.totalResponseMs += ev.responseMs;
  est.firstAssessed = est.firstAssessed ?? ev.at;
  est.lastAssessed = ev.at;
  if (ev.correct && ev.hintsUsed === 0 && ev.source !== 'guided') {
    est.maxIndependentDifficulty = Math.max(est.maxIndependentDifficulty, ev.difficulty);
  }
  if (ev.confidence) {
    const p = CONF_P[ev.confidence];
    const c = est.calibration;
    est.calibration = {
      n: c.n + 1,
      brierSum: c.brierSum + (p - (ev.correct ? 1 : 0)) ** 2,
      overconfident: c.overconfident + (p >= 0.75 && !ev.correct ? 1 : 0),
      underconfident: c.underconfident + (p <= 0.55 && ev.correct ? 1 : 0),
    };
  }
  if (ev.misconceptionId) {
    est.misconceptions = { ...est.misconceptions, [ev.misconceptionId]: (est.misconceptions[ev.misconceptionId] ?? 0) + 1 };
  }
  const rec: EvidenceRecord = {
    at: ev.at, source: ev.source, difficulty: ev.difficulty, score, weight, hintsUsed: ev.hintsUsed,
    responseMs: ev.responseMs, confidence: ev.confidence, fingerprint: ev.fingerprint,
  };
  est.recent = [...est.recent.slice(-24), rec];

  // memory: one retrieval event per 6 hours (first attempt of a sitting counts)
  const last = est.memory.lastReview;
  if (!last || ev.at - last >= 6 * 3_600_000) {
    const rating = !ev.correct ? 'again' : ev.hintsUsed > 0 ? 'hard' : 'good';
    est.memory = review(est.memory, rating, ev.at);
  } else if (!ev.correct && est.memory.due && est.memory.due > ev.at + DAY) {
    // repeated failure within a sitting pulls the next review closer
    est.memory = { ...est.memory, due: ev.at + DAY };
  }
  return est;
}

/** Thresholds are relative to the skill's own mastery target. */
function cuts(target: number) {
  return {
    weak: Math.max(1.75, target - 3),
    proficient: target - 1.5,
    mastered: target - 0.25,
  };
}

/**
 * Demonstrated level: a conservative posterior quantile, capped by the
 * hardest task actually solved independently. Nobody is credited above
 * what they have shown.
 */
export function demonstratedLevel(est: SkillEstimate): number {
  const q = quantile(est.posterior, 0.35);
  return Math.min(q, est.maxIndependentDifficulty + 0.5);
}

export function statusOf(skill: Skill, est: SkillEstimate | undefined, relevance: CurriculumRelevance): AssessmentStatus {
  if (!est || est.evidenceCount === 0) {
    return relevance === 'unavailable' || relevance === 'other-track' ? 'LOCKED' : 'UNKNOWN';
  }
  const c = cuts(skill.masteryTarget);
  const m = mean(est.posterior);
  const s = sd(est.posterior);
  const L = demonstratedLevel(est);
  if (est.evidenceCount >= 2 && m < c.weak && s < 1.45) return 'WEAK';
  if (est.evidenceCount < 2 || s > 1.3) return 'ASSESSED';
  if (L >= c.mastered && est.evidenceCount >= 3 && s <= 1.1) return 'MASTERED';
  if (L >= c.proficient) return 'PROFICIENT';
  if (m >= c.weak) return 'DEVELOPING';
  return 'WEAK';
}

export function deriveView(skill: Skill, est: SkillEstimate | undefined, relevance: CurriculumRelevance, now: number): SkillView {
  if (!est || est.evidenceCount === 0) {
    return {
      skillId: skill.id,
      status: statusOf(skill, est, relevance),
      relevance,
      mastery: 0,
      ability: null,
      uncertainty: 3,
      confidence: 0,
      evidenceCount: 0,
      estimatedDifficulty: null,
      lastAssessed: null,
      retrievability: 0,
      forgettingRisk: 0,
      due: null,
      accuracy: null,
      inferredSupport: !!est?.inferredSupport,
    };
  }
  const m = mean(est.posterior);
  const s = sd(est.posterior);
  const L = demonstratedLevel(est);
  const R = retrievability(est.memory, now);
  const top = Object.entries(est.misconceptions).sort((a, b) => b[1] - a[1])[0];
  return {
    skillId: skill.id,
    status: statusOf(skill, est, relevance),
    relevance,
    mastery: clamp01((L - 1) / (skill.masteryTarget - 1)),
    ability: m,
    uncertainty: s,
    confidence: clamp01(1 - (s - 0.4) / 1.6) * clamp01(est.evidenceCount / 4),
    evidenceCount: est.evidenceCount,
    estimatedDifficulty: clamp(Math.round(Math.max(1, L)), 1, 7) as Difficulty,
    lastAssessed: est.lastAssessed,
    retrievability: R,
    forgettingRisk: 1 - R,
    due: est.memory.due,
    accuracy: est.attempts ? est.correct / est.attempts : null,
    inferredSupport: !!est.inferredSupport,
    topMisconception: top && top[1] >= 2 ? top[0] : undefined,
  };
}

export const STATUS_ORDER: AssessmentStatus[] = ['LOCKED', 'UNKNOWN', 'ASSESSED', 'WEAK', 'DEVELOPING', 'PROFICIENT', 'MASTERED'];

export const STATUS_LABEL: Record<AssessmentStatus, string> = loc({
  UNKNOWN: B('Unknown', 'Ukjent'), ASSESSED: B('Assessed (provisional)', 'Vurdert (foreløpig)'), WEAK: B('Weak', 'Svak'), DEVELOPING: B('Developing', 'Under utvikling'),
  PROFICIENT: B('Proficient', 'Dyktig'), MASTERED: B('Mastered', 'Mestret'), LOCKED: B('Unavailable', 'Utilgjengelig'),
});
